import React from 'react';
import { Component } from '../../../dispatcher/modules';
import PuzzleInput from '../../UI/fields/input/PuzzleInput';
import { GridColumnMenuItemGroup } from '@progress/kendo-react-grid';
import { Keys } from '@progress/kendo-react-common';
import {
  debounce,
  head,
  isEqual,
  isFunction
} from 'lodash';
import { MenuItem } from '@progress/kendo-react-layout';
import PuzzleMenu from '../../layout/menu/PuzzleMenu';
import PuzzleRemoteMenu from '../../layout/menu/PuzzleRemoteMenu';
import PuzzleFakeCheckbox from '../../UI/checkbox/PuzzleFakeCheckbox';
import HBox from '../../layout/container/h_box/HBox';
import withVisibility from '../../hoc/withVisibility';
import PuzzleRemoteList from '../../layout/menu/PuzzleRemoteList';
import { getTextSearchMethod } from '../../../utility';
import { COLUMN_MENU_PROPS, COLUMN_MENU_STATE } from './Types';

class ColumnMenu extends Component<COLUMN_MENU_PROPS, COLUMN_MENU_STATE> {
  static defaultProps = {
    clearFilterAction: 'columnFilterChanged',
    data: [],
    filterable: true,
    onFilterChange: () => {},
    singleSelectList: false,
    sortable: true,
    values: []
  };
  private containerRef: React.RefObject<HTMLSpanElement | null> = React.createRef();
  state: COLUMN_MENU_STATE = {
    data: this.props.data.slice(),
    inputValue: head(this.props.values),
    listSearch: ''
  };

  moduleDidMount() {
    const {
      column,
      setActiveHeader
    } = this.props;

    setActiveHeader && setActiveHeader(column.field);
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      data,
      isVisible
    } = this.props;

    if (!isEqual(data, prevProps.data)) {
      this.setState({
        data: data.slice()
      }, this.filterData);
    }

    if (this.state.listSearch !== prevState.listSearch) {
      this.filterData();
    }

    if (isVisible && (isVisible !== prevProps.isVisible) && this.containerRef.current) {
      const el: HTMLElement = this.containerRef.current.querySelector('[tabindex]:not([tabindex="-1"])');
      if (el !== null) {
        el.focus();
      }
    }
  }

  componentWillUnmount = () => {
    const {
      setActiveHeader
    } = this.props;
    setActiveHeader && setActiveHeader('');
  };

  private filterData = () => {
    const { listSearch } = this.state;
    const { searchType } = this.props;
    const searchFunction = getTextSearchMethod(searchType);
    this.setState({
      data: this.props.data.filter(({ name }) => name.toLowerCase()[searchFunction](listSearch.toLowerCase()))
    });
  };

  private isChecked = (value: string) => this.props.values.indexOf(value) > -1;

  private onClear = (event: React.KeyboardEvent | React.MouseEvent) => {
    if ((event.type === 'click') || [Keys.enter, Keys.space].includes((event as React.KeyboardEvent).keyCode)) {
      event.preventDefault();
      const {
        column,
        onFilterChange,
        values,
        mappingFilterName
      } = this.props;

      if (values.length) {
        onFilterChange(mappingFilterName ?? column.field, []);
      }

      this.setState({
        listSearch: ''
      });
    }
  };

  private onFilterChange = () => {
    const {
      column,
      onFilterChange,
      mappingFilterName
    } = this.props;
    const { inputValue } = this.state;

    onFilterChange(mappingFilterName ?? column.field, inputValue ? [inputValue] : []);
  };

  private onInputDelete = (event: React.KeyboardEvent | React.MouseEvent) => {
    if ((event.type === 'click') || [Keys.enter, Keys.space].includes((event as React.KeyboardEvent).keyCode)) {
      event.preventDefault();
      this.setState({ inputValue: '' }, this.onFilterChange);
    }
  };

  private onInputChange = ({ target }) => {
    if (this.props.type === 'input-numeric' && isNaN(target.value)) {
      this.setState({
        inputValue: ''
      });
    } else {
      this.setState({
        inputValue: target.value
      }, this.onInputChangeBuffered);
    }
  }

  private onInputChangeBuffered = debounce(this.onFilterChange, 1000);

  private onListSearch = ({ target }) => {
    this.setState({
      listSearch: target.value
    });
  };

  private onListSearchDelete = () => {
    this.setState({ listSearch: '' });
  };

  private onSort = (dir?: string) => {
    const {
      closeMenu,
      column,
      onSortChange
    } = this.props;

    closeMenu.call();
    onSortChange({
      sort: dir ? [{
        field: column.field,
        dir
      }] : []
    });
  }

  private renderClearFilter = () => this.props.filterable && (
    <GridColumnMenuItemGroup>
      {this.props.type.includes('list') && this.renderSelectedItems()}
      <HBox
        alignItemsCenter
        className="k-columnmenu-item k-link tp-text-underline tp-clear-filter"
        onKeyDown={['input', 'input-numeric'].includes(this.props.type) ? this.onInputDelete : this.onClear}
        onClick={['input', 'input-numeric'].includes(this.props.type) ? this.onInputDelete : this.onClear}
        role="button"
        tabIndex={0}
      >
        {this.translate('clearFilter')}
      </HBox>
    </GridColumnMenuItemGroup>
  );

  private renderFilter = () => {
    const { filterable, type } = this.props;
    let returnValue = null;

    if (filterable && type) {
      let filter;

      switch (type) {
        case 'input':
        case 'input-numeric':
          filter = this.renderInputFilter();
          break;
        case 'list':
        case 'list-search':
        case 'virtual-list':
        case 'virtual-dom-list':
        case 'virtual-list-search':
        case 'virtual-dom-list-search':
          filter = this.renderListFilter(type.includes('list-search'));
          break;
        default:
          break;
      }

      if (filter) {
        returnValue = (
          <GridColumnMenuItemGroup>
            {filter}
          </GridColumnMenuItemGroup>
        );
      }
    }

    return returnValue;
  };

  private renderListFilter = (hasSearch: boolean) => {
    const virtual = this.props.type.includes('virtual');
    const virtualDom = this.props.type.includes('virtual-dom');
    const { data = [] } = this.state;
    let props: any = {
      className: (this.props.sortable || hasSearch) ? 'tp-border-top' : '',
      onSelect: this.onSelect.bind(this),
      trackVisibility: false,
      vertical: true
    };
    let wrapperProps: any = {};

    if (virtual) {
      const {
        dataItemKey = 'value',
        parseData,
        requestConfig,
        textField = 'name',
        searchType
      } = this.props;

      props = {
        className: props.className,
        data,
        dataItemKey,
        itemRender: this.renderListItem,
        menuProps: { ...props, className: '' },
        parseData,
        requestConfig,
        textField,
        searchType
      };
    } else {
      wrapperProps.className = 'tp-menu-with-overflow';
    }

    return (
      <div {...wrapperProps}>
        {hasSearch && this.renderListSearch()}
        {virtual
          ? this.getRemoteMenu(props, virtualDom)
          : data.length > 0 &&
          (
            <PuzzleMenu {...props}>
              {data.map(this.renderListItem)}
            </PuzzleMenu>
          )
        }
      </div>
    );
  };

  private getRemoteMenu = (props, virtualDom) => {
    if (virtualDom) {
      return (
        <PuzzleRemoteList
          {...props}
          listSearch={this.state.listSearch}
        />
      );
    } else {
      return (
        <PuzzleRemoteMenu {...props} />
      );
    }
  }

  protected onSelect({ item }: any) {
    const { value } = item.data;
    const { column, mappingFilterName } = this.props;
    let values = this.props.values.slice();
    const index = values.indexOf(value);

    if (index > -1) {
      values.splice(index, 1);
    } else {
      this.props.singleSelectList ? values = [value] : values.push(value);
      values.sort((a, b) => a.localeCompare(b));
    }

    this.props.onFilterChange(mappingFilterName ? mappingFilterName : column.field, values);
  }

  private renderListItem = (data: any) => (
    <MenuItem
      cssClass={this.isChecked(data.value) ? 'k-selected' : ''}
      data={data}
      key={data.value ?? data._index}
      render={data.value ? this.renderMenuItem : () => data[this.props.column.field]}
    />
  );

  private renderListSearch = () => (
    <HBox className="k-columnmenu-item tp-search-columnmenu-item">
      <PuzzleInput
        size={'small'}
        actionBtnSettings={[{
          buttonType: 'delete',
          size: 'small',
          onClick: this.onListSearchDelete
        }]}
        inputClassName="tp-dropdown-list-input"
        onChange={this.onListSearch}
        placeholder={this.translate('inputPlaceholder')}
        value={this.state.listSearch}
      />
    </HBox>
  );

  private renderMenuItem = ({ item }: any) => {
    const { data } = item;
    const checked = this.isChecked(data.value);

    return (
      <PuzzleFakeCheckbox
        checked={checked}
        label={data.name}
      />
    );
  };

  private renderSelectedItems = () => {
    const count = this.props.values.length;
    const translationKey = count === 0 ? 'defaultSelected' : 'selected';

    return (
      <div
        className="k-columnmenu-item tp-count-items tp-text-semibold tp-column-menu-text"
      >
        {this.translate(translationKey, { count })}
      </div>
    );
  };

  private renderInputFilter = () => (
    <HBox className="k-columnmenu-item tp-search-columnmenu-item">
      <PuzzleInput
        size={'small'}
        actionBtnSettings={[{
          buttonType: 'delete',
          size: 'small',
          onClick: this.onInputDelete
        }]}
        placeholder={this.translate('inputPlaceholder')}
        value={this.state.inputValue}
        onChange={this.onInputChange.bind(this)}
      />
    </HBox>

  );

  /**
   *  Conditionally renders a button that allows resetting the sorting of a column in a sortable table.
   *  The button is only rendered if sortable prop is true.
   */
  private renderResetSort = () => this.props.sortable && (
    <HBox
      className="k-columnmenu-item tp-sort-columnmenu-item"
      onClick={() => this.onSort()}
      onKeyDown={({ keyCode }) => {
        if ([Keys.enter, Keys.space].includes(keyCode)) {
          this.onSort();
        }
      }}
    >
      <i
        aria-hidden
        className={'tp-icon tp-close'}
      />
      <span className="tp-column-menu-text">
        {this.translate('resetSort')}
      </span>
    </HBox>
  );

  private renderSorter = () => this.props.sortable && (
    <GridColumnMenuItemGroup>
      {['asc', 'desc'].map((dir: string) => (
        <HBox
          className="k-columnmenu-item tp-sort-columnmenu-item"
          key={dir}
          onClick={() => {
            this.onSort(dir);
          }}
          onKeyDown={({ keyCode }) => {
            if ([Keys.enter, Keys.space].includes(keyCode)) {
              this.onSort(dir);
            }
          }}
          tabIndex={0}
        >
          <i
            aria-hidden
            className={`tp-icon tp-${dir === 'desc' ? 'dsc' : dir}`}
          />
          <span className="tp-column-menu-text">
            {this.props.dynamicTranslationForSort ? this.props.dynamicTranslationForSort[dir] : this.translate(dir)}
          </span>
        </HBox>
      ))}
      {(this.props.withResetSort && this.props.isSorted) ? this.renderResetSort() : null}
    </GridColumnMenuItemGroup>
  );

  render() {
    return (
      <span
        // className={'tp-menu-with-overflow'}
        ref={this.containerRef}
      >
        {this.renderSorter()}
        {this.renderFilter()}
        {this.renderClearFilter()}
      </span>
    );
  }
}

export default withVisibility(ColumnMenu);
